// libraries
import React, { useState, useEffect } from 'react'

// components
import Section from '/react4xp/components/Section'
import Button from '/react4xp/components/Button'
import Link from '/react4xp/components/Link'
import Form from '/react4xp/components/Form/Form'
import Input from '/react4xp/components/Form/Input'
import Search from '/react4xp/components/Form/Search'
import TextArea from '/react4xp/components/Form/TextArea'
import Upload from '/react4xp/components/Form/Upload'
import Checkbox from '/react4xp/components/Form/Checkbox'
import Select from '/react4xp/components/Form/Select'

import dayjs from 'dayjs'

interface IdeaSubmitFormProps {
    section?: {
		bgColor?: string
		isWhite?: boolean
	}
    title?: string
    form?: {
        onSuccess?: (data: any) => void
        isFormData?: boolean,
        closed?: {
            isClosed?: boolean
            title?: string
            text?: string
            cta?: {
                text?: string
                url?: string
            }
        }
        endpoint: string
        popupSuccessContent?: {
            closeButtonText?: string
            title?: string
            content?: string
            actionButtons?: {
                primary?: {
                    text?: string
                }
            }
        }
        popupErrorContent?: {
            closeButtonText?: string
            title?: string
            content?: string
            actionButtons?: {
                primary?: {
                    text?: string
                }
            }
        }
        inputs?: {
            title?: {
                id?: string
                label?: string
                name?: string
                required?: boolean
                errorMessages?: {
                    required?: string
                }
            }
            volunteerRegister?: {
                id?: string
                label?: string
                name?: string
                required?: boolean
                errorMessages?: {
                    required?: string
                    invalidSuggestion?: string
                    alreadySelected?: string
                    alreadySelectedUrlText?: string
                    alreadySelectedUrl?: string
                }
                endpoint?: string
                isValidVolunteerRegisterEndpoint?: string
            }
            extraField?: {
                id?: string
                label?: string
                name?: string
                required?: boolean
                errorMessages?: {
                    required?: string
                }
            }
            message?: {
                id?: string
                label?: string
                name?: string
                required?: boolean
                minLength?: number
                maxLength?: {
                    limit: number
                    text: string
                }
                errorMessages?: {
                    required?: string
                    maxLength?: string
                    minLength?: string
                }
            }
            upload?: {
                id?: string
                label?: string
                name?: string
                accept?: string
                maxFileSize?: number
                required?: boolean
                errorMessages?: {
                    required?: string
                    maxFileSize?: string
                }
            }
            checkbox?: {
                id?: string
                name?: string
                errorMessages?: {
                    required?: string
                }
                text?: string
            }
            selects?: Array<{
                label?: string
                id?: string
                size?: string
                name?: string
                required?: boolean
                errorMessages?: {
                    required?: string
                }
                options?: Array<{
                    value?: string
                    text?: string
                }>
            }>
            submitText?: string
            cancelText?: string
            cancelUrl?: string
        }
    }
}

export default function IdeaSubmitForm({
    section,
    title,
    form
}: IdeaSubmitFormProps) {

    const [isImageUploaded, setIsImageUploaded] = useState('')

    // change the above state when the user uploads a photo
    // we will use this to make the checkbox below the upload field required / mandatory or not
    const handleUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsImageUploaded(e.target.value)
    }

	useEffect(() => {
		document.addEventListener('fileDeleted', () => {
			setIsImageUploaded('')
		})
	}, [])

    // volunteer register
    const [volunteerRegisterSelected, setVolunteerRegisterSelected] = useState(null)

    return (
        <Section bgColor={section?.bgColor || 'red'} paddingTop='small'>
            <div className='container'>
                <div className='row'>
                    <div className='col-md-10 offset-md-1 col-lg-8 offset-lg-2 col-xl-6 offset-xl-3'>
                        <div className='page-user__content bg-white'>

                            {title && (
                                <h1 className='page-user__title h2'>
                                    {title}
                                </h1>
                            )}

                            {form?.closed?.isClosed ? (
                                <div className='page-idea--submit__closed'>

                                    {form?.closed?.title && (
                                        <h2 className='page-idea--submit__closed-title h3'>
                                            {form?.closed?.title}
                                        </h2>
                                    )}

                                    {form?.closed?.text && (
                                        <div
                                            className='page-idea--submit__closed-text'
                                            dangerouslySetInnerHTML={{
                                                __html: form?.closed?.text
                                            }}
                                        />
                                    )}

                                    {form?.closed?.cta && (
                                        <Button
                                            color='black'
                                            hover='red'
                                            type='submit'
                                            noUnderline
                                            href={form?.closed?.cta?.url}
                                        >
                                            {form?.closed?.cta?.text}
                                        </Button>
                                    )}

                                </div>
                            ) : (
                                <Form
                                    isFormData={form.isFormData}
                                    onSuccess={form.onSuccess}
                                    endpoint={form?.endpoint}
                                    className='page-idea__form'
                                    popupSuccessContent={{
                                        hasCloseButton: true,
                                        closeButtonText: form?.popupSuccessContent?.closeButtonText,
                                        title: form?.popupSuccessContent?.title,
                                        content: form?.popupSuccessContent?.content,
                                        actionButtons: {
                                            primary: {
                                                text: form?.popupSuccessContent?.actionButtons?.primary?.text,
                                                closeOnClick: true,
                                            }
                                        }
                                    }}
                                    popupErrorContent={{
                                        hasCloseButton: true,
                                        closeButtonText: form?.popupErrorContent?.closeButtonText,
                                        title: form?.popupErrorContent?.title,
                                        content: form?.popupErrorContent?.content,
                                        actionButtons: {
                                            primary: {
                                                text: form?.popupErrorContent?.actionButtons?.primary?.text,
                                                closeOnClick: true,
                                                //onClick: () => { alert('test') }
                                            }
                                        }
                                    }}
                                    additionalValues={{
                                        'volunteer-register-id': volunteerRegisterSelected?.id,
                                        'volunteer-register-name': volunteerRegisterSelected?.name
                                    }}
                                >

                                    {form?.inputs?.title && (
                                        <Input
                                            name={form?.inputs?.title?.name}
                                            id={form?.inputs?.title?.id}
                                            label={form?.inputs?.title?.label}
                                            type='text'
                                            required={form?.inputs?.title?.required}
                                            size='full-width'
                                            errorMessages={{
                                                required: form?.inputs?.title?.errorMessages?.required
                                            }}
                                        />
                                    )}

                                    {form?.inputs?.volunteerRegister && (
                                        <Search
                                            name={form?.inputs?.volunteerRegister?.name}
                                            id={form?.inputs?.volunteerRegister?.id}
                                            label={form?.inputs?.volunteerRegister?.label}
                                            type='text'
                                            required={form?.inputs?.volunteerRegister?.required}
                                            size='full-width'
                                            errorMessages={{
                                                required: form?.inputs?.volunteerRegister?.errorMessages?.required,
                                                invalidSuggestion: form?.inputs?.volunteerRegister?.errorMessages?.invalidSuggestion,
                                                alreadySelected: form?.inputs?.volunteerRegister?.errorMessages?.alreadySelected,
                                                alreadySelectedUrlText: form?.inputs?.volunteerRegister?.errorMessages?.alreadySelectedUrlText,
                                                alreadySelectedUrl: form?.inputs?.volunteerRegister?.errorMessages?.alreadySelectedUrl
                                            }}
                                            endpoint={form?.inputs?.volunteerRegister?.endpoint}
                                            isValidVolunteerRegisterEndpoint={form?.inputs?.volunteerRegister?.isValidVolunteerRegisterEndpoint}
                                            onSuggestionSelect={(suggestion) => {
                                                setVolunteerRegisterSelected(suggestion)
                                            }}
                                        />
                                    )}

                                    {form?.inputs?.extraField && (
                                        <Input
                                            name={form?.inputs?.extraField?.name}
                                            id={form?.inputs?.extraField?.id}
                                            label={form?.inputs?.extraField?.label}
                                            type='text'
                                            required={form?.inputs?.extraField?.required}
                                            size='full-width'
                                            errorMessages={{
                                                required: form?.inputs?.extraField?.errorMessages?.required
                                            }}
                                        />
                                    )}

                                    {form?.inputs?.message && (
                                        <TextArea
                                            id={form?.inputs?.message?.id}
                                            label={form?.inputs?.message?.label}
                                            name={form?.inputs?.message?.name}
                                            required={form?.inputs?.message?.required}
                                            size='full-width'
                                            minLength={form?.inputs?.message?.minLength}
                                            maxLength={{
                                                limit: form?.inputs?.message?.maxLength?.limit,
                                                text: form?.inputs?.message?.maxLength?.text
                                            }}
                                            errorMessages={{
                                                required: form?.inputs?.message?.errorMessages?.required,
                                                maxLength: form?.inputs?.message?.errorMessages?.maxLength,
                                                minLength: form?.inputs?.message?.errorMessages?.minLength
                                            }}
                                        />
                                    )}

                                    {form?.inputs?.upload && (
                                        <Upload
                                            id={form?.inputs?.upload?.id}
                                            label={form?.inputs?.upload?.label}
                                            name={form?.inputs?.upload?.name}
                                            className='page-idea--submit__margin-bottom'
                                            accept={form?.inputs?.upload?.accept}
                                            maxFileSize={form?.inputs?.upload?.maxFileSize}
                                            onChange={handleUploadChange}
                                            required={form?.inputs?.upload?.required}
                                            errorMessages={{
                                                required: form?.inputs?.upload?.errorMessages?.required,
                                                maxFileSize: form?.inputs?.upload?.errorMessages?.maxFileSize
                                            }}
                                        />
                                    )}
                                    
                                    {form?.inputs?.checkbox && (
                                        <Checkbox
                                            id={form?.inputs?.checkbox?.id}
                                            name={form?.inputs?.checkbox?.name}
                                            required={form?.inputs?.upload?.required || isImageUploaded !== ''}
                                            className='page-idea__margin-bottom'
                                            errorMessages={{
                                                required: form?.inputs?.checkbox?.errorMessages?.required
                                            }}
                                        >
                                            {form?.inputs?.checkbox?.text}
                                        </Checkbox>
                                    )}

                                    {form?.inputs?.selects?.map((select, i) => (
                                        <Select
                                            key={i}
                                            defaultValue=''
                                            label={select.label}
                                            id={select.id}
                                            size='full-width'
                                            name={select.name}
                                            required={select.required}
                                            errorMessages={{
                                                required: select.errorMessages?.required
                                            }}
                                        >
                                            <option value='' disabled>Velg en</option>
                                            {select.options?.map((option, i2) => (
                                                <option
                                                    key={i2}
                                                    value={option.value}
                                                >
                                                    {option.text}
                                                </option>
                                            ))}
                                        </Select>
                                    ))}

                                    <Input id='idea-date' type='hidden' name='idea-date' value={dayjs().format('YYYY-MM-DDTHH:mm:ss')} />
                                    <Input id='idea-type' type='hidden' name='idea-type' value='nominations' />

                                    <div className='page-idea--submit__two-buttons'>

                                        <Button
                                            color='black'
                                            hover='red'
                                            type='submit'
                                            noUnderline
                                        >
                                            {form?.inputs?.submitText}
                                        </Button>

                                        <Link
                                            href={form?.inputs?.cancelUrl}
                                            text={form?.inputs?.cancelText}
                                        />

                                    </div>

                                </Form>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </Section>
    )
}